import { ApplicationApi } from 'api';
import { infinityCacheTime } from 'api/ReactQueryOptions';
import { V1 } from 'api/v1/V1';
import { getLocalizedDateFormat } from 'library/formatters/DateFormatter';

export type ServiceProtocolActivitiesQueryParams = Parameters<
	InstanceType<typeof V1>['getServiceContent']
>[0];

export const getServiceProtocolInitialDatesQuery = (
	api: ApplicationApi,
	drivelineId: number
) => ({
	queryKey: serviceProtocolKeys.initialDates(drivelineId),
	queryFn: async () =>
		(await api.getServiceProtocolInitialDates({ drivelineId })).data,
});

export const getServiceProtocolQuery = (
	api: ApplicationApi,
	id: number,
	includeEvents?: boolean
) => ({
	queryKey: serviceProtocolKeys.data(id, includeEvents ?? false),
	queryFn: async () =>
		(await api.getServiceProtocol(id, { includeEvents })).data,
});

export const getServiceProtocolActivitiesQuery = (
	api: ApplicationApi,
	params: ServiceProtocolActivitiesQueryParams
) => ({
	queryKey: serviceProtocolKeys.activities(params),
	queryFn: async () => {
		const response = await api.getServiceContent(params);
		return response.status === 204 ? null : response.data;
	},
});

export const getSupportedProtocolLanguagesQuery = (api: ApplicationApi) => ({
	queryKey: serviceProtocolKeys.languages(),
	queryFn: async () => {
		const response = await api.getSupportedProtocolLanguageList();
		return response.data;
	},
	...infinityCacheTime<Record<string, string>>(),
});

export const serviceProtocolKeys = {
	all: ['serviceProtocol'] as const,
	activities: (params: ServiceProtocolActivitiesQueryParams) => [
		...serviceProtocolKeys.all,
		JSON.stringify(params, (_, value) =>
			value instanceof Date ? getLocalizedDateFormat(value) : value
		),
	],
	initialDates: (drivelineId: number) => [
		...serviceProtocolKeys.all,
		'initialDates',
		drivelineId,
	],
	data: (id: number, includeEvents: boolean) => [
		...serviceProtocolKeys.single(id),
		'data',
		includeEvents,
	],
	single: (id: number) => [...serviceProtocolKeys.all, id] as const,
	languages: () => [...serviceProtocolKeys.all, 'languages'] as const,
};
